import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, ChevronLeft, ChevronRight, ArrowLeft, BarChart3, Trophy } from 'lucide-react'
import { api } from '../../lib/api'
import { ScanFilters, FilterState } from '../../components/scans/ScanFilters'
import { ScanGallery } from '../../components/scans/ScanGallery'
import { Scan } from '../../components/scans/ScanCard'
import { QuickScan } from '../../components/parent/QuickScan'

interface ScanHistoryResponse {
  student: {
    id: number
    name: string
  }
  scans: Scan[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

interface ScanStats {
  student: {
    id: number
    name: string
  }
  total_scans: number
  by_subject: Record<string, number>
  by_type: Record<string, number>
  total_xp_from_scans: number
  uncategorized_count: number
  recent_count: number
}

export default function ChildScans() {
  const { studentId } = useParams<{ studentId: string }>()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<FilterState>({
    startDate: '',
    endDate: '',
    subject: '',
    scanType: '',
    uncategorized: false,
  })

  const studentIdNum = parseInt(studentId || '0', 10)

  // Build query params
  const queryParams = new URLSearchParams()
  queryParams.set('page', page.toString())
  queryParams.set('limit', '20')
  if (filters.startDate) queryParams.set('start_date', filters.startDate)
  if (filters.endDate) queryParams.set('end_date', filters.endDate)
  if (filters.subject) queryParams.set('subject', filters.subject)
  if (filters.scanType) queryParams.set('scan_type', filters.scanType)
  if (filters.uncategorized) queryParams.set('uncategorized', 'true')

  const { data, isLoading, error } = useQuery<ScanHistoryResponse>({
    queryKey: ['child-scan-history', studentIdNum, page, filters],
    queryFn: async () => {
      const response = await api.get(
        `/api/parent/students/${studentIdNum}/scans?${queryParams}`
      )
      return response.data
    },
    enabled: studentIdNum > 0,
  })

  const { data: stats } = useQuery<ScanStats>({
    queryKey: ['child-scan-stats', studentIdNum],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentIdNum}/scans/stats`)
      return response.data
    },
    enabled: studentIdNum > 0,
  })

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters)
    setPage(1)
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-400">Failed to load scans</p>
        <Link to="/parent" className="btn btn-primary mt-4">
          Back to Dashboard
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/parent/student/${studentIdNum}`}
          className="p-2 rounded-lg bg-surface-card hover:bg-surface-hover transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-quest-primary/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-quest-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {data?.student.name || stats?.student.name || 'Loading...'}'s Scans
            </h1>
            <p className="text-sm text-gray-400">
              {stats ? `${stats.total_scans} total scans` : 'Loading...'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-lg font-bold">{stats.total_scans}</p>
              <p className="text-xs text-gray-400">Total Scans</p>
            </div>
          </div>

          <div className="card flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-lg font-bold">{stats.recent_count}</p>
              <p className="text-xs text-gray-400">This Week</p>
            </div>
          </div>

          <div className="card flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-quest-accent/20 flex items-center justify-center">
              <span className="text-quest-accent font-bold">XP</span>
            </div>
            <div>
              <p className="text-lg font-bold">+{stats.total_xp_from_scans}</p>
              <p className="text-xs text-gray-400">XP Earned</p>
            </div>
          </div>

          {stats.uncategorized_count > 0 && (
            <button
              onClick={() => handleFilterChange({ ...filters, uncategorized: true })}
              className="card flex items-center gap-3 border-orange-500/30 bg-orange-900/10 hover:bg-orange-900/20 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <span className="text-lg">!</span>
              </div>
              <div className="text-left">
                <p className="text-lg font-bold text-orange-400">
                  {stats.uncategorized_count}
                </p>
                <p className="text-xs text-orange-300">Need categorization</p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Subject breakdown */}
      {stats && Object.keys(stats.by_subject).length > 0 && (
        <div className="card">
          <h3 className="font-medium mb-3">By Subject</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.by_subject).map(([subject, count]) => (
              <button
                key={subject}
                onClick={() => handleFilterChange({ ...filters, subject })}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  filters.subject === subject
                    ? 'bg-quest-primary text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {subject} ({count})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <ScanFilters
        onFilterChange={handleFilterChange}
        initialFilters={filters}
        showUncategorizedToggle={true}
      />

      {/* Scan gallery */}
      <ScanGallery
        scans={data?.scans || []}
        isLoading={isLoading}
        studentId={studentIdNum}
        isParentView={true}
      />

      {/* Pagination */}
      {data && data.pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">
            Showing {(data.pagination.page - 1) * data.pagination.limit + 1}-
            {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{' '}
            {data.pagination.total}
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              className="p-2 rounded-lg bg-surface-card hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <span className="px-3 py-1 text-sm">
              {page} / {data.pagination.pages}
            </span>

            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= data.pagination.pages}
              className="p-2 rounded-lg bg-surface-card hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Quick Scan FAB */}
      {studentIdNum > 0 && (
        <QuickScan
          studentId={studentIdNum}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['child-scan-history', studentIdNum] })
            queryClient.invalidateQueries({ queryKey: ['child-scan-stats', studentIdNum] })
          }}
        />
      )}

      {/* Bottom Navigation Tabs */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface-base border-t border-gray-800 p-2">
        <div className="max-w-4xl mx-auto flex justify-around">
          <Link
            to={`/parent/student/${studentIdNum}`}
            className="flex flex-col items-center gap-1 p-2 text-gray-400 hover:text-gray-200"
          >
            <FileText className="w-5 h-5" />
            <span className="text-xs">Homework</span>
          </Link>
          <Link
            to={`/parent/student/${studentIdNum}/scans`}
            className="flex flex-col items-center gap-1 p-2 text-quest-primary"
          >
            <BarChart3 className="w-5 h-5" />
            <span className="text-xs">Scans</span>
          </Link>
          <Link
            to={`/parent/student/${studentIdNum}/progress`}
            className="flex flex-col items-center gap-1 p-2 text-gray-400 hover:text-gray-200"
          >
            <Trophy className="w-5 h-5" />
            <span className="text-xs">Progress</span>
          </Link>
        </div>
      </div>
    </div>
  )
}
